<template>
    <div class="gap-md col-span-full flex flex-col">
        <div class="gap-y-md grid w-full grid-cols-2 justify-between">
            <h1>Users</h1>

            <Button class="flex justify-self-end" @click="routeToCreationProcess()">
                Create a new user
                <i class="i-plus" />
            </Button>

            <div class="gap-sm flex">
                <!-- Toggle treatment from notifications-view (ITDEVELOP-2730): active tab
                     on the negative (charcoal) role, inactive on the soft surface. -->
                <Button
                    variant="ghost"
                    :class="
                        currentView !== 'established'
                            ? 'bg-soft text-primary'
                            : 'bg-negative text-negative hover:bg-negative'
                    "
                    @click="router.replace({ query: { view: 'established' } })"
                >
                    Established
                </Button>
                <Button
                    variant="ghost"
                    :class="
                        currentView !== 'invited'
                            ? 'bg-soft text-primary'
                            : 'bg-negative text-negative hover:bg-negative'
                    "
                    @click="router.replace({ query: { view: 'invited' } })"
                >
                    Invitations
                </Button>
            </div>
        </div>

        <!-- content -->
        <TransitionGroup name="user-list" tag="div" class="gap-md flex flex-col" mode="out-in">
            <UserInvitationList
                v-if="currentView === 'invited'"
                :users="invitedUsers"
                :search-input="searchInput"
                :can-approve="adminStore.canApprove"
                @approve-users="approveUsers"
                @decline-users="declineUsers"
            />

            <UserEstablishedList
                v-else-if="currentView === 'established'"
                :users="establishedUsers"
                :search-input="searchInput"
            />
        </TransitionGroup>
    </div>
</template>

<script lang="ts" setup>
import { Button } from '@/components/ui/button';
import UserInvitationList from '@/components/admin/users/user-invitation-list.vue';
import UserEstablishedList from '@/components/admin/users/user-established-list.vue';
import { useAdminStore } from '@/stores/admin';
import { type AdminUserEstablished, type AdminUserInvitation } from '@/stores/store-interfaces';
import { computed, onMounted, ref, watch } from 'vue';
import router from '@/router/admin-router';
import { useRoute } from 'vue-router';

const adminStore = useAdminStore();

const establishedUsers = ref<AdminUserEstablished[]>([]);
const invitedUsers = ref<AdminUserInvitation[]>([]);

const route = useRoute();
type UserView = 'established' | 'invited';

const currentView = computed<UserView>(() => (route.query.view === 'invited' ? 'invited' : 'established'));
const searchInput = ref('');

async function loadUsers(view: UserView) {
    if (view === 'invited') {
        invitedUsers.value = await adminStore.getUserInvitations();
        return;
    }

    establishedUsers.value = await adminStore.getEstablishedUsers();
}

onMounted(async () => {
    if (route.query.view === undefined) {
        await router.replace({ query: { ...route.query, view: 'established' } });
    }

    await loadUsers(currentView.value);
});

const approveUsers = async (ids: number[]) => {
    const results: { successful: number[]; failed: number[] } = {
        successful: [],
        failed: [],
    };

    let toast = '';
    if (ids.length === 0) {
        toast = 'No users selected';
        adminStore.setToastMessage(toast, 'error');
        return;
    }

    Promise.all(
        ids.map(async (id) => {
            if (await adminStore.approveUserInvitation(id)) {
                results.successful.push(id);
            } else {
                results.failed.push(id);
            }
        }),
    )
        .then(() => {
            toast = `${results.successful.length} user${
                results.successful.length > 1 ? 's' : ''
            } approved, ${results.failed.length} user${results.failed.length > 1 ? 's' : ''} failed to approve`;

            adminStore.setToastMessage(toast, results.failed.length === 0 ? 'check' : 'error');
        })
        .then(async () => {
            establishedUsers.value = await adminStore.getEstablishedUsers();
            invitedUsers.value = await adminStore.getUserInvitations();
            console.log(invitedUsers.value);
        });
};

const declineUsers = async (ids: number[], comment?: string) => {
    if (ids.length === 0) {
        adminStore.setToastMessage('No users selected', 'error');
        return;
    }

    const results: { successful: number[]; failed: number[] } = {
        successful: [],
        failed: [],
    };

    await Promise.all(
        ids.map(async (id) => {
            try {
                await adminStore.declineUserInvitation(id, comment);
                results.successful.push(id);
            } catch {
                results.failed.push(id);
            }
        }),
    );

    const noun = (n: number) => `invitation${n === 1 ? '' : 's'}`;
    const toast =
        results.failed.length === 0
            ? `${results.successful.length} ${noun(results.successful.length)} declined`
            : `${results.successful.length} ${noun(results.successful.length)} declined, ${results.failed.length} failed`;
    adminStore.setToastMessage(toast, results.failed.length === 0 ? 'check' : 'error');

    invitedUsers.value = await adminStore.getUserInvitations();
};

const routeToCreationProcess = () => {
    router.push({ name: 'user-creation-process' });
};

watch(currentView, (view, previousView) => {
    if (view !== previousView) {
        void loadUsers(view);
    }
});
</script>
